import {TrainRideInfo, RideTags} from '@/data/train-store';

export class TrainAPI {
  public static async searchRides(options: SearchRidesOptions): Promise<TrainRideInfo[]> {
    const {fromId, toId, date, hour} = options;
    const baseUrl = 'https://www.rail.co.il/apiinfo/api/Plan/GetRoutes';

    let dateStr = `${date.getFullYear()}`;
    dateStr += `${('0' + (date.getMonth() + 1)).slice(-2)}`;
    dateStr += `${('0' + date.getDate()).slice(-2)}`;

    const searchDate = new Date(date);
    searchDate.setHours(Number(hour.split(':')[0]));
    searchDate.setMinutes(Number(hour.split(':')[1]));

    const [hourStr, minutesStr] = hour.split(':');
    const Hour = `${('0' + hourStr).slice(-2)}${('0' + minutesStr).slice(-2)}`;

    const query = objToQuery({
      OId: fromId,
      TId: toId,
      Date: dateStr, // 20190603
      Hour, // 0900
      isGoing: true
    });

    const response = await fetch(`${baseUrl}${query}`);
    const json = await response.json();

    const rawRoutes = json && json.Data && json.Data.Routes as any[] | undefined;
    if (!rawRoutes) throw new Error(API_ERROR.INVALID_RESPONSE);
    if (!rawRoutes.length) throw new Error(API_ERROR.NO_RESULTS);

    const {Origin, Destination, Date: rawDate} = json.Data.Details;
    const rawLoad = json.Data.Omasim;

    return rawRoutes
      .map((route) => {
        const {Train, EstTime} = route;

        const firstTrain = Train[0];
        const lastTrain = Train[Train.length - 1];
        const {DepartureTime, StopStations: StopStationsFirst} = firstTrain; // first train
        const {ArrivalTime, StopStations: StopStationsLast} = lastTrain; // last train

        const [durationHoursStr, DurationMinutesStr] = EstTime.split(':');
        const durationMinutes = (Number(durationHoursStr) * 60) + Number(DurationMinutesStr);

        const [departHour, departMinute] = DepartureTime.split(' ')[1].split(':');
        const fromHour = `${departHour}:${departMinute}`;

        const [arriveHour, arriveMinute] = ArrivalTime.split(' ')[1].split(':');
        const toHour = `${arriveHour}:${arriveMinute}`;
        const fromPlatform = firstTrain.Platform;
        const toPlatform = lastTrain.DestPlatform;
        // const {Platform: fromPlatform} = StopStationsFirst.find((x: any) => {
        //   console.log(x.StationId === Origin, x.StationId, Origin);
        //   return x.StationId === Origin;
        // });
        // const {Platform: toPlatform} = StopStationsLast.find((x: any) => x.StationId === Destination);

        const TrainLoads = rawLoad.find((x: any) => x.TrainNumber === Number(firstTrain.Trainno));
        let load = -1;
        if (TrainLoads) {
          const stationInfo = TrainLoads.Stations.find((x: any) => x.StationNumber === Number(Origin));
          if (stationInfo && stationInfo.OmesPercent >= 0) {
            load = Math.max(Math.min(Math.round(stationInfo.OmesPercent * 100), 100), 0);
          }
        }

        const tags = [];
        if (firstTrain.TrainParvariBenironi || lastTrain.TrainParvariBenironi) {
          tags.push(RideTags.REARGUARD);
        }

        let switches;
        if (firstTrain !== lastTrain) {
          // has switches
          switches = [];
          for (let i = 0; i < Train.length; i++) {
            const trainA = Train[i];
            const trainB = Train[i + 1];
            if (!trainB) break;

            const candidateSwitches: TrainRideInfo['switches'] = [];
            for (const stationA of trainA.StopStations) {
              const arrivalStampA = parseDateStr(stationA.ArrivalTime);
              for (const stationB of trainB.StopStations) {
                if (stationA.StationId !== stationB.StationId) continue;
                const arrivalStampB = parseDateStr(stationB.DepartureTime);
                const timeDiff = arrivalStampB - arrivalStampA;
                if (timeDiff < 0) continue;
                candidateSwitches.push({
                  stationId: stationA.StationId,
                  waitTimeMinutes: Math.round(timeDiff / 1000 / 60),
                  fromPlatform: stationA.Platform,
                  toPlatform: stationB.Platform
                });
              }
            }

            candidateSwitches.sort((a, b) => {
              if (a.waitTimeMinutes >= 5 && b.waitTimeMinutes < 5) return -1;
              if (b.waitTimeMinutes >= 5 && a.waitTimeMinutes < 5) return 1;

              const timeDiff = Math.abs(b.waitTimeMinutes - a.waitTimeMinutes);
              if (timeDiff < 5) {
                const isSamePlatformA = a.fromPlatform === a.toPlatform;
                const isSamePlatformB = b.fromPlatform === b.toPlatform;

                if (isSamePlatformA && !isSamePlatformB) return -1;
                if (isSamePlatformB && !isSamePlatformA) return 1;
              }

              return a.waitTimeMinutes - b.waitTimeMinutes;
            });

            const switchPoint = candidateSwitches[0];
            if (switchPoint) {
              switches.push(switchPoint);
            } else {
              // throw new Error(`Couldn't find switching station ${JSON.stringify(Train)}`);
            }
          }
        }

        const departDate = new Date(parseDateStr(rawDate));
        const fromDate = `${departDate.getDate()}-${departDate.getMonth() + 1}-${departDate.getFullYear()}`;

        const departTime = new Date(parseDateStr(DepartureTime));
        const minutesFromSearch = Math.round((departTime.getTime() - searchDate.getTime()) / 60000);

        const lineNumberMatchers = [
          /^(\d{4})(\d{4})$/,
          /^(\d{3})(\d{4})$/,
          /^(\d{4})(\d{3})$/,
          /^(\d{3})(\d{3})$/
        ];

        let firstTrainOrigin = '';
        let firstTrainDestination = '';
        for (const matcher of lineNumberMatchers) {
          const [match, from, to] = matcher.exec(firstTrain.LineNumber) || [null, '', ''];
          if (!match || !from || !to) continue;
          firstTrainOrigin = from;
          firstTrainDestination = to;
          break;
        }

        return {
          id: firstTrain.Trainno,
          active: true,
          fromId: Origin,
          toId: Destination,
          fromHour,
          toHour,
          fromDate,
          durationMinutes,
          fromPlatform,
          toPlatform,
          load,
          status: 'on-time', // ToDo: extract from JSON
          delayMinutes: 0, // ToDo: extract from JSON
          tags,
          switches,
          originId: firstTrainOrigin,
          destinationId: firstTrainDestination,
          minutesFromSearch
        };
      });
  }
}

// hoist line

function objToQuery(obj: object) {
  let query = '';
  for (const [key, value] of Object.entries(obj)) {
    query += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }

  query = query.replace(/^&/, '');

  return query ? `?${query}` : '';
}

function parseDateStr(timeStr: string) {
  const [
    _,
    dayStr,
    monthStr,
    yearStr,
    hourStr,
    minutesStr,
    secondsStr
  ] = timeStr.match(/^(\d+)\/(\d+)\/(\d+) (\d+):(\d+):(\d+)/) || [] as any;

  if (!dayStr || !monthStr || !yearStr || !hourStr || !minutesStr || !secondsStr) {
    throw new Error('Invalid raw date str');
  }

  return new Date(
    Number(yearStr),
    Number(monthStr) - 1,
    Number(dayStr),
    Number(hourStr),
    Number(minutesStr),
    Number(secondsStr)
  ).getTime();
}

export const enum API_ERROR {
  INVALID_RESPONSE = 'INVALID_RESPONSE',
  NO_RESULTS = 'NO_RESULTS'
}

interface SearchRidesOptions {
  fromId: string;
  toId: string;
  date: Date;
  hour: string;
}
